import { useRef } from 'react';
import Playhead from './Playhead';
import { formatTimecode } from './Timecode';

// §2.6 Scrub bar with comment markers (accent dots, range tails for range comments) and an instant playhead.
// markers: [{ id, at, end?, label }]; ticks: [seconds] (e.g. cut-list picks on this clip).
// Keyboard: ←/→ 1 s, Shift ←/→ 5 s, Home/End. Seeking is instant (§2.4).
export default function ScrubBar({ duration, position, onSeek, markers = [], activeMarker, onMarker, ticks = [], format = 'mss', fps = 29.97, label = 'Seek' }) {
  const ref = useRef(null);
  const pct = t => `${(Math.min(duration, Math.max(0, t)) / (duration || 1)) * 100}%`;
  const seekAt = clientX => {
    const r = ref.current.getBoundingClientRect();
    onSeek?.(Math.min(duration, Math.max(0, ((clientX - r.left) / r.width) * duration)));
  };
  const onKey = e => {
    const step = e.shiftKey ? 5 : 1;
    if (e.key === 'ArrowRight') { e.preventDefault(); onSeek?.(Math.min(duration, position + step)); }
    if (e.key === 'ArrowLeft') { e.preventDefault(); onSeek?.(Math.max(0, position - step)); }
    if (e.key === 'Home') { e.preventDefault(); onSeek?.(0); }
    if (e.key === 'End') { e.preventDefault(); onSeek?.(duration); }
  };
  return (
    <div className="ui ui-scrub" ref={ref}
      onPointerDown={e => { if (e.target.closest('.ui-scrub__marker')) return; e.currentTarget.setPointerCapture(e.pointerId); seekAt(e.clientX); }}
      onPointerMove={e => { if (e.buttons === 1 && e.currentTarget.hasPointerCapture(e.pointerId)) seekAt(e.clientX); }}>
      <span className="ui-scrub__track" />
      <span className="ui-scrub__done" style={{ width: pct(position) }} />
      {ticks.map(t => <span key={t} className="ui-scrub__tick" style={{ left: pct(t) }} />)}
      {markers.filter(m => m.end != null).map(m => <span key={`t${m.id}`} className="ui-scrub__tail" style={{ left: pct(m.at), width: `calc(${pct(m.end)} - ${pct(m.at)})` }} />)}
      <span role="slider" tabIndex={0} aria-label={label} aria-valuemin={0} aria-valuemax={Math.round(duration)} aria-valuenow={Math.round(position)}
        aria-valuetext={formatTimecode(position, format, fps)} onKeyDown={onKey} className="ui-sr">{formatTimecode(position, format, fps)}</span>
      <Playhead position={position / (duration || 1)} />
      {markers.map(m => (
        <button key={m.id} type="button" className="ui-scrub__marker" style={{ left: pct(m.at) }} aria-current={m.id === activeMarker}
          aria-label={`${m.label || 'Comment'} at ${formatTimecode(m.at, format, fps)}`} onClick={() => { onMarker?.(m); onSeek?.(m.at); }}>
          <span className="ui-scrub__dot" />
        </button>
      ))}
    </div>
  );
}
